import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  Alert,
  Button,
  Skeleton,
  Card,
  CardContent,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import InboxIcon from "@mui/icons-material/Inbox";

import {
  useAppDispatch,
  useAppSelector,
  fetchUserInstances,
  fetchActiveInstances,
  terminateInstance,
  removeInstance,
  showToast,
} from "../store";
import { instanceService } from "../services";
import { InstanceCard } from "../components/instances";
import { ConfirmDialog } from "../components/common";
import { MAX_ACTIVE_INSTANCES } from "../services";
import { ServiceInstance } from "../types";
import { usePolling } from "../hooks";
import { POLLING_INTERVAL } from "../utils";

type FilterType = "all" | "active" | "inactive";
type SortType = "newest" | "oldest" | "expiring" | "name";

const InstancesPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.auth);
  const { instances, activeCount, isLoading, isTerminating, error } =
    useAppSelector((state) => state.instances);

  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortType>("newest");
  const [confirmInstance, setConfirmInstance] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      dispatch(fetchUserInstances({ userId: user.id, page: 0, size: 50 }));
      dispatch(fetchActiveInstances(user.id));
    }
  }, [user, dispatch]);

  const refreshInstances = useCallback(() => {
    if (user) {
      dispatch(fetchUserInstances({ userId: user.id, page: 0, size: 50 }));
      dispatch(fetchActiveInstances(user.id));
    }
  }, [user, dispatch]);

  // Always poll - catches PENDING->FAILED transitions too
  usePolling(refreshInstances, POLLING_INTERVAL, !!user);

  const handleRefresh = () => {
    if (user) {
      dispatch(fetchUserInstances({ userId: user.id, page: 0, size: 50 }));
      dispatch(fetchActiveInstances(user.id));
    }
  };

  const handleTerminate = async () => {
    if (!confirmInstance) return;
    try {
      await dispatch(terminateInstance(confirmInstance)).unwrap();
      dispatch(
        showToast({
          message: "Instance terminated successfully",
          severity: "success",
        }),
      );
      setConfirmInstance(null);
    } catch {
      dispatch(
        showToast({
          message: "Failed to terminate instance",
          severity: "error",
        }),
      );
    }
  };

  const handleRemove = async (instanceId: string) => {
    try {
      await instanceService.deleteInstance(instanceId);
      dispatch(removeInstance(instanceId));
      dispatch(showToast({ message: "Instance deleted", severity: "success" }));
    } catch {
      dispatch(
        showToast({ message: "Failed to delete instance", severity: "error" }),
      );
    }
  };

  const allInstances = instances || [];

  // Filter
  const statusFiltered = allInstances.filter((instance: ServiceInstance) => {
    if (filter === "active")
      return ["PENDING", "PROVISIONING", "RUNNING", "STOPPED"].includes(
        instance.status,
      );
    if (filter === "inactive")
      return ["EXPIRED", "TERMINATED", "FAILED"].includes(instance.status);
    return true;
  });

  // Search
  const searched = search
    ? statusFiltered.filter(
        (i: ServiceInstance) =>
          (i.serviceName || i.service?.name || "")
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          i.instanceId.toLowerCase().includes(search.toLowerCase()),
      )
    : statusFiltered;

  // Sort
  const sorted = [...searched].sort(
    (a: ServiceInstance, b: ServiceInstance) => {
      switch (sort) {
        case "oldest":
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        case "expiring":
          return (
            new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime()
          );
        case "name":
          return (a.serviceName || "").localeCompare(b.serviceName || "");
        case "newest":
        default:
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
      }
    },
  );

  const inactiveCount = allInstances.length - activeCount;

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-start" },
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
            }}
          >
            My Instances
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
            }}
          >
            Manage your running Docker instances
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Chip
            label={`${activeCount} / ${MAX_ACTIVE_INSTANCES} active`}
            color={activeCount >= MAX_ACTIVE_INSTANCES ? "error" : "primary"}
            variant="outlined"
            size="small"
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate("/services")}
            disabled={activeCount >= MAX_ACTIVE_INSTANCES}
          >
            New Instance
          </Button>
        </Box>
      </Box>
      {/* Filter + Search + Sort bar */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, v) => v && setFilter(v)}
          size="small"
        >
          <ToggleButton value="all">All ({allInstances.length})</ToggleButton>
          <ToggleButton value="active">Active ({activeCount})</ToggleButton>
          <ToggleButton value="inactive">
            Inactive ({inactiveCount})
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          placeholder="Search by name or ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{
            width: { xs: "100%", sm: 260 },
            "& .MuiOutlinedInput-root": { bgcolor: "#ffffff" },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            },
          }}
        />

        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
          <Select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortType)}
            displayEmpty
            startAdornment={
              <SortIcon
                sx={{ fontSize: 16, color: "text.secondary", mr: 0.5 }}
              />
            }
            sx={{ bgcolor: "#ffffff" }}
          >
            <MenuItem value="newest">Newest first</MenuItem>
            <MenuItem value="oldest">Oldest first</MenuItem>
            <MenuItem value="expiring">Expiring soon</MenuItem>
            <MenuItem value="name">Service name</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 3 }}
              >
                <Skeleton
                  variant="rectangular"
                  width={48}
                  height={48}
                  sx={{ borderRadius: 2 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="30%" height={24} />
                  <Skeleton variant="text" width="20%" />
                </Box>
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={24}
                  sx={{ borderRadius: 1 }}
                />
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
      {/* Empty State */}
      {!isLoading && sorted.length === 0 && (
        <Paper
          sx={{
            p: 6,
            borderRadius: 3,
            border: "2px dashed #e5e7eb",
            textAlign: "center",
            bgcolor: "#ffffff",
          }}
        >
          <InboxIcon sx={{ fontSize: 56, color: "#d1d5db", mb: 2 }} />
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: "text.secondary",
              fontWeight: 600,
            }}
          >
            {search
              ? "No matching instances"
              : filter === "all"
                ? "No instances yet"
                : filter === "active"
                  ? "No active instances"
                  : "No inactive instances"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 3,
              maxWidth: 400,
              mx: "auto",
            }}
          >
            {search
              ? `No instances match "${search}". Try a different search term.`
              : filter === "all"
                ? "Browse the service catalog to launch your first Docker instance and start building."
                : "Instances will appear here based on your filter selection."}
          </Typography>
          {filter === "all" && !search && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/services")}
            >
              Browse Services
            </Button>
          )}
          {search && (
            <Button variant="outlined" onClick={() => setSearch("")}>
              Clear Search
            </Button>
          )}
        </Paper>
      )}
      {/* Instance List */}
      {!isLoading && sorted.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {sorted.map((instance: ServiceInstance) => (
            <InstanceCard
              key={instance.id}
              instance={instance}
              onTerminate={(id) => setConfirmInstance(id)}
              onRemove={handleRemove}
              isTerminating={isTerminating}
            />
          ))}
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textAlign: "center",
              mt: 1,
            }}
          >
            Showing {sorted.length} of {allInstances.length} instances
          </Typography>
        </Box>
      )}
      <ConfirmDialog
        open={!!confirmInstance}
        title="Terminate Instance?"
        message="Are you sure you want to terminate this instance? This action cannot be undone and you will lose access to this service immediately."
        confirmText="Terminate"
        confirmColor="error"
        onConfirm={handleTerminate}
        onCancel={() => setConfirmInstance(null)}
        isLoading={isTerminating}
      />
    </Box>
  );
};

export default InstancesPage;
